import { isSymbol } from "@shared/types/utils";
import {
  BacktestingRunType,
  BacktestingStatus,
  LanguageNameSpaces,
  OpenMessage,
} from "@shared/types";
import useLiveBacktesting from "@shared/features/bots/BotForm/Backtesting/useLiveBacktesting";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE } from "@shared/features/bots/BotForm/common/constants";
import { BacktestActions } from "@shared/features/bots/BotForm/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useSharedBacktestingActionsProps {
  data?: BacktestingRunType;
  openDeleteModal: () => void;
  openMessage: OpenMessage;
  isSelected?: boolean;
  navigateToBacktestDetails: (
    botId: number,
    backtestId: number,
    subGroupId?: number,
    accountId?: number,
    fromActionModal?: boolean
  ) => void;
  navigateToFormBacktesting: (liveBacktestingRun: BacktestingRunType) => void;
}

const useSharedBacktestingActions = ({
  data,
  openDeleteModal,
  openMessage,
  isSelected,
  navigateToBacktestDetails,
  navigateToFormBacktesting,
}: useSharedBacktestingActionsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { account, subGroup, initializeFormValues, backtestBotData } =
    useContext(BotFormContext) || {};
  const { liveBacktestingData, status } = useLiveBacktesting({ data });

  const openExtraDetailsDrawer = (fromActionModal?: boolean) => {
    if (!data) return;
    if (status === BacktestingStatus.FAILED) {
      openMessage(t(FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE), "warning");
    } else {
      navigateToBacktestDetails(
        data.bot_id,
        data.id,
        subGroup?.id,
        !isSymbol(account) ? account?.client_id : undefined,
        fromActionModal
      );
    }
  };

  const applyBacktestConfiguration = () => {
    if (!data) return;
    if (isSelected) {
      initializeFormValues?.(backtestBotData);
    } else if (liveBacktestingData) {
      navigateToFormBacktesting(liveBacktestingData);
    }
  };

  const menuItems = [
    {
      key: BacktestActions.SHOW_DETAILS,
      label: t("backtesting.actions.show_details"),
      onClick: () => openExtraDetailsDrawer(true),
    },
    {
      key: BacktestActions.APPLY_CONFIGURATION,
      label: t("backtesting.actions.apply_configuration"),
      onClick: applyBacktestConfiguration,
    },
    {
      key: BacktestActions.DELETE,
      label: t("backtesting.actions.delete"),
      onClick: openDeleteModal,
    },
  ];

  return { menuItems, openExtraDetailsDrawer, status };
};

export default useSharedBacktestingActions;
